import { motion } from "motion/react";

import { usePrefersReducedMotion } from "../../lib/usePrefersReducedMotion";
import { Clip, type ClipSet } from "../Clip";
import { inView, rise, stagger } from "./motion";
import styles from "./SelfHost.module.css";

const DOCS = "https://docs.gryt.chat/docs";

/**
 * Hosting from the app, start to finish, as the whole take: the point is that the recording
 * never leaves the app. Encoded with `--width 2200 --fps 30 --poster-at 2.9`.
 */
const CREATE: ClipSet = {
  src: "/home/create-server.mp4",
  av1: "/home/create-server.av1.mp4",
  poster: "/home/create-server.poster.webp",
};

const CREATE_SHOWS =
  "An empty Gryt client, then Add a server, a name typed into Create your " +
  "server with the port already filled in, a running server with its General " +
  "channel open and a message sent in it, and finally the Servers on your " +
  "network page with that server already listed";

/**
 * Four ways in, ordered by how little you have to know, and none of them a wall of shell.
 * The twelve-line Compose block lives in the quick-start guide, where it stays correct.
 */
const WAYS = [
  {
    name: "From the app",
    detail:
      "The client you downloaded is already a server. Name it, press create, read the address out. No terminal at all.",
    href: `${DOCS}/host/from-the-app`,
    linkLabel: "Hosting from the app",
  },
  {
    name: "Windows",
    detail:
      "A zip, one config file and a batch script. Node.js is the only thing you have to install yourself.",
    href: `${DOCS}/host/windows`,
    linkLabel: "Windows guide",
  },
  {
    name: "Linux",
    detail:
      "Four commands and a server is up. The database is SQLite, so a backup is one directory. There's a catch worth reading about first.",
    href: `${DOCS}/host/quick-start`,
    linkLabel: "Quick start",
  },
  {
    name: "Docker",
    detail:
      "A compose file and an .env. There's a Helm chart too, if you're the sort of person who has a cluster.",
    href: `${DOCS}/host/docker-compose`,
    linkLabel: "Deployment docs",
  },
];

const MORE = [
  { label: "No domain, just an IP", href: `${DOCS}/host/no-domain` },
  { label: "Behind a Cloudflare Tunnel", href: `${DOCS}/host/cloudflare-tunnel` },
  { label: "Over Tailscale", href: `${DOCS}/host/tailscale` },
  { label: "Monitoring", href: `${DOCS}/host/monitoring` },
];

export function SelfHost() {
  const reduced = usePrefersReducedMotion() ?? false;

  return (
    <section className={styles.section} id="self-host">
      <motion.div className={styles.inner} variants={stagger(reduced)} {...inView}>
        <motion.p className={styles.eyebrow} variants={rise(reduced)}>
          Self-hosting
        </motion.p>
        <motion.h2 className={styles.heading} variants={rise(reduced)}>
          Put it on a machine that stays on.
        </motion.h2>
        <motion.p className={styles.sub} variants={rise(reduced)}>
          Hosting from the app is fine for an evening with friends. But if you
          want it still running after you shut your laptop, put it on something
          that stays on. Pick whichever of these sounds like you.
        </motion.p>

        <motion.figure className={styles.capture} variants={rise(reduced)}>
          <Clip {...CREATE} alt={CREATE_SHOWS} width={2200} height={1212} />
          <figcaption>
            The whole thing, uncut. Name it, keep the port it picked, press
            create, say something, and there it is on the network for everyone
            else. It stops when you close the app. That's what the four below
            are for.
          </figcaption>
        </motion.figure>

        <div className={styles.grid}>
          {WAYS.map((w) => (
            <motion.a
              key={w.name}
              className={styles.card}
              href={w.href}
              target="_blank"
              rel="noreferrer"
              variants={rise(reduced)}
            >
              <h3>{w.name}</h3>
              <p>{w.detail}</p>
              <span className={styles.cardLink}>
                {w.linkLabel} <span aria-hidden="true">→</span>
              </span>
            </motion.a>
          ))}
        </div>

        <motion.ul className={styles.more} variants={rise(reduced)}>
          {MORE.map((m) => (
            <li key={m.href}>
              <a href={m.href} target="_blank" rel="noreferrer">
                {m.label} <span aria-hidden="true">→</span>
              </a>
            </li>
          ))}
        </motion.ul>
      </motion.div>
    </section>
  );
}
